import React, { useEffect, useState, forwardRef, useContext } from "react";
import LocalizationContext from "../../Localization/LanguageContext";
import { ILocalizationContext } from "../../Localization/Models/ILocalizationContext";
import { UsernameValidation } from "../../Models/PolicyInfo";
import ValidationError from "../ValidationError";

interface Props {
  errorMessage: string;
  inputId: string;
  inputValue: string | number | readonly string[];
  isValid: UsernameValidation;
  maxLength: number;
  type: string;
  onFocus?: () => void;
  onChange: React.Dispatch<React.SetStateAction<string>>;
  setUsernameValid: React.Dispatch<React.SetStateAction<UsernameValidation>>;
}

const UsernameInput = forwardRef<HTMLInputElement, Props>(
  (props: Props, ref) => {

    const [errorMsg, setErrorMsg] = useState(props.errorMessage);
    const { getValue, locale } = useContext(LocalizationContext) as ILocalizationContext;
    
    useEffect(() => {
      if (!props.isValid.touched)
        return;
      else if (props.isValid.nonLegitChars  && !props.isValid.empty) {
        setErrorMsg(getValue('UsernameField', 'InvalidCharacters'));
      } else if (props.isValid.isShort && !props.isValid.empty) {
        setErrorMsg(getValue('UsernameField', 'InvalidLength'));
      } else if (props.isValid.invalid && !props.isValid.empty) {
        setErrorMsg(getValue('UsernameField', 'Unavailable'));
      }
      else {
        setErrorMsg(props.errorMessage);
      }
    }, [props.isValid]);
 
    // Need this so that error messages properly switch between en/es
    useEffect(() => {
      var value = props.inputValue as string;
      var pattern = /^[A-Za-z0-9]+$/;
      props.setUsernameValid((prev) => {
        return {
          invalid: prev.invalid,
          touched: prev.touched,
          isShort: value.trim().length < 8,
          empty: value.trim().length === 0,
          nonLegitChars: !pattern.test(value.toString())
        };
      });
    }, [locale]);

    const fireOnBlur = (value: string) => {
      var pattern = /^[A-Za-z0-9]+$/;
      props.setUsernameValid((prev) => {
        return {
          invalid: prev.invalid,
          touched: true,
          isShort: value.trim().length < 8,
          empty: value.trim().length === 0,
          nonLegitChars: !pattern.test(value.toString())
        };
      });
    };

    const onChange = (value: string) => {
      var pattern = /^[A-Za-z0-9]+$/;
      if (value.length < 21) props.onChange(value);
      props.setUsernameValid((prev) => {
        return {
          invalid: prev.invalid,
          touched: true,
          isShort: value.length < 8,
          empty: value.length === 0,
          nonLegitChars: !pattern.test(value.toString())
        };
      });
    };

    const checkValid = () => {
      return (
        (props.isValid.empty ||
          props.isValid.invalid ||
          props.isValid.isShort ||
          props.isValid.nonLegitChars) &&
        props.isValid.touched
      );
    };

    return (
      <>
        <input
          id={props.inputId}
          ref={ref}
          type={props.type}
          className={`w-100 p-2 ${
            checkValid() ? "input-error" : "text-input"
          }`}
          maxLength={props.maxLength}
          value={props.inputValue}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => fireOnBlur(e.target.value)}
          onFocus={props.onFocus}
        />
        <ValidationError isInvalid={checkValid()} message={errorMsg} />
      </>
    );
  }
);

export default UsernameInput;
